import React, { useEffect } from 'react';
import { useSystem } from '../../contexts/SystemContext';
import { useSources } from '../../contexts/SourcesContext';
import { useAuth } from '../../hooks/useAuth';

export function OverviewTab() {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { sources } = useSources();
  const {
    systemStatus,
    queueStats,
    costData,
    loading,
    error,
    fetchSystemData,
  } = useSystem();

  // Top-level AdminDashboard manages polling. Keep a manual refresh only.
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      fetchSystemData();
    }
  }, [isAuthenticated, authLoading, fetchSystemData]);

  if (error) {
    return (
      <div className="tc-card p-6">
        <div className="text-center text-red-600">
          <p>Error: {error}</p>
          <button onClick={fetchSystemData} className="tc-btn tc-btn-primary mt-4">
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (loading && !systemStatus) {
    return (
      <div className="tc-card p-6">
        <div className="text-center">
          <div className="tc-spinner mb-4"></div>
          <p className="tc-text-secondary">Loading system overview...</p>
        </div>
      </div>
    );
  }

  const activeSources = sources.filter(s => s.isActive).length;
  const totalJobs = (queueStats?.pending || 0) + (queueStats?.processing || 0);
  const dailyPercentage = costData?.daily?.percentage || 0;

  return (
    <div className="space-y-6">
      {/* System Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* System Status Card */}
        <div className="tc-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-lg">🟢</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium tc-text-secondary truncate">
                  System Status
                </dt>
                <dd className="text-lg font-medium tc-text-primary">
                  {systemStatus?.status || 'Active'}
                </dd>
                {systemStatus?.uptime && (
                  <dd className="text-xs tc-text-tertiary">
                    Uptime: {systemStatus.uptime}
                  </dd>
                )}
              </dl>
            </div>
          </div>
        </div>

        {/* Queue Status Card */}
        <div className="tc-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                totalJobs > 0 ? 'bg-orange-100' : 'bg-green-100'
              }`}>
                <span className={`text-lg ${
                  totalJobs > 0 ? 'text-orange-600' : 'text-green-600'
                }`}>
                  {totalJobs > 0 ? '⏳' : '✅'}
                </span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium tc-text-secondary truncate">
                  Queue Status
                </dt>
                <dd className="text-lg font-medium tc-text-primary">
                  {totalJobs} Active Jobs
                </dd>
                {queueStats && (
                  <dd className="text-xs tc-text-tertiary">
                    {queueStats.pending} pending, {queueStats.processing} processing
                  </dd>
                )}
              </dl>
            </div>
          </div>
        </div>

        {/* Daily Cost Card */}
        <div className="tc-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                dailyPercentage > 90 ? 'bg-red-100' : dailyPercentage > 70 ? 'bg-yellow-100' : 'bg-green-100'
              }`}>
                <span className={`text-lg ${
                  dailyPercentage > 90 ? 'text-red-600' : dailyPercentage > 70 ? 'text-yellow-600' : 'text-green-600'
                }`}>
                  💰
                </span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium tc-text-secondary truncate">
                  Daily Cost
                </dt>
                <dd className="text-lg font-medium tc-text-primary">
                  ${costData?.daily?.spent?.toFixed(2) || '0.00'}
                </dd>
                <dd className="text-xs tc-text-tertiary">
                  of ${costData?.daily?.budget?.toFixed(2) || '0.00'} budget
                </dd>
              </dl>
            </div>
          </div>
        </div>

        {/* Active Sources Card */}
        <div className="tc-card p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-lg">📡</span>
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium tc-text-secondary truncate">
                  Active Sources
                </dt>
                <dd className="text-lg font-medium tc-text-primary">
                  {activeSources} of {sources.length}
                </dd>
                <dd className="text-xs tc-text-tertiary">
                  News sources monitored
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div>

      {/* Additional System Information */}
      {(queueStats?.processing || 0) > 0 && (
        <div className="tc-card p-4 border-l-4 border-orange-400 bg-orange-50">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="tc-spinner w-5 h-5"></div>
            </div>
            <div className="ml-3">
              <p className="text-sm text-orange-800">
                <strong>Processing:</strong> {queueStats?.processing || 0} job(s) currently running
              </p>
              <p className="text-xs text-orange-600 mt-1">
                Articles are being analyzed. Check the Queue tab for details.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* System Health Summary */}
      <div className="tc-card p-6">
        <h3 className="text-lg font-medium tc-text-primary mb-4">System Health Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="text-2xl font-bold tc-text-primary">
              {sources.length}
            </div>
            <div className="text-sm tc-text-secondary">Total Sources</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold tc-text-primary">
              {queueStats?.completed || 0}
            </div>
            <div className="text-sm tc-text-secondary">Jobs Completed Today</div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold tc-text-primary">
              {dailyPercentage.toFixed(1)}%
            </div>
            <div className="text-sm tc-text-secondary">Daily Budget Used</div>
          </div>
        </div>
      </div>
    </div>
  );
}
